/**
 * The side-by-side compare as rows (OPEN-144 §2c): the saved ticket and the parked one,
 * lined up field by field, tank by tank, line by line, with the rows that differ marked.
 * Pure, so the review panel, the job modal and the Sync page render the same thing.
 */

import { formatTimestamp } from '../../lib/timestamp';
import type { CompareTicketView } from './use-sync-conflicts';
import { formatNumber } from '@lodex-pro/shared/number-format';

export type CompareSection = 'Ticket' | 'Tank' | 'Product' | 'Extra Services';

export interface CompareRow {
  readonly section: CompareSection;
  readonly label: string;
  readonly saved: string;
  readonly parked: string;
  readonly differs: boolean;
}

const dash = '—';
const num = (v: number | null | undefined): string => (v == null ? dash : String(v));
const text = (v: string | null | undefined): string => (v == null || v === '' ? dash : v);
const gauge = (ft: number | null, inch: number | null): string =>
  ft == null && inch == null ? dash : `${String(ft ?? 0)}' ${String(inch ?? 0)}"`;

function row(section: CompareSection, label: string, saved: string, parked: string): CompareRow {
  return { section, label, saved, parked, differs: saved !== parked };
}

/** Pair two lists by position (or index), so tank 1 sits next to tank 1. */
function pairs<T extends { readonly position: number | null }>(
  a: readonly T[],
  b: readonly T[],
): Array<[T | undefined, T | undefined]> {
  const key = (t: T, i: number): number => t.position ?? i;
  const keys = new Set<number>([...a.map(key), ...b.map(key)]);
  return [...keys]
    .sort((x, y) => x - y)
    .map((k) => [a.find((t, i) => key(t, i) === k), b.find((t, i) => key(t, i) === k)]);
}

export function compareRows(
  saved: CompareTicketView | null,
  parked: CompareTicketView,
  timeZone: string,
): CompareRow[] {
  const s = saved;
  const p = parked;
  const at = (iso: string | null | undefined): string =>
    iso == null ? dash : formatTimestamp(iso, timeZone);
  const who = (t: CompareTicketView | null): string =>
    t === null ? dash : text(t.capturedByName ?? null);
  const gps = (t: CompareTicketView | null): string =>
    t === null || t.gpsLat == null || t.gpsLng == null
      ? dash
      : `${formatNumber(t.gpsLat, { decimals: 5 })}, ${formatNumber(t.gpsLng, { decimals: 5 })}`;

  const rows: CompareRow[] = [
    row('Ticket', 'Captured', at(s?.capturedAt), at(p.capturedAt)),
    row('Ticket', 'By', who(s), who(p)),
    row('Ticket', 'Method', text(s?.captureMethod), text(p.captureMethod)),
    row('Ticket', 'Reference #', text(s?.referenceId), text(p.referenceId)),
    row('Ticket', 'GPS', gps(s), gps(p)),
    row(
      'Ticket',
      'Net BBL',
      s === null ? dash : formatNumber(s.netBbl, { decimals: 2 }),
      formatNumber(p.netBbl, { decimals: 2 }),
    ),
  ];

  pairs(s?.tanks ?? [], p.tanks).forEach(([a, b], i) => {
    const n = `Tank ${String(i + 1)}`;
    rows.push(
      row('Tank', `${n} name`, text(a?.tankName), text(b?.tankName)),
      row('Tank', `${n} water (bbl)`, num(a?.waterVolumeBbl), num(b?.waterVolumeBbl)),
      row('Tank', `${n} oil (bbl)`, num(a?.oilVolumeBbl), num(b?.oilVolumeBbl)),
      row('Tank', `${n} before (bbl)`, num(a?.beforeBbl), num(b?.beforeBbl)),
      row('Tank', `${n} after (bbl)`, num(a?.afterBbl), num(b?.afterBbl)),
      row(
        'Tank',
        `${n} top gauge`,
        a === undefined ? dash : gauge(a.topGaugeFeet, a.topGaugeInches),
        b === undefined ? dash : gauge(b.topGaugeFeet, b.topGaugeInches),
      ),
      row(
        'Tank',
        `${n} bottom gauge`,
        a === undefined ? dash : gauge(a.bottomGaugeFeet, a.bottomGaugeInches),
        b === undefined ? dash : gauge(b.bottomGaugeFeet, b.bottomGaugeInches),
      ),
      row('Tank', `${n} color cut`, text(a?.colorCut), text(b?.colorCut)),
      row('Tank', `${n} seal off`, text(a?.sealOffNumber), text(b?.sealOffNumber)),
      row('Tank', `${n} seal on`, text(a?.sealOnNumber), text(b?.sealOnNumber)),
      row('Tank', `${n} product`, text(a?.productName), text(b?.productName)),
    );
  });

  pairs(s?.lineItems ?? [], p.lineItems).forEach(([a, b], i) => {
    const name = b?.productName ?? a?.productName ?? `Product ${String(i + 1)}`;
    rows.push(row('Product', name, num(a?.quantity), num(b?.quantity)));
  });

  // Extra Services are matched by charge, not position. The swap does NOT change them
  // (Corbin, option A): a difference is fixed in billing.
  const ids = new Set([
    ...(s?.serviceCharges ?? []).map((c) => c.serviceChargeId),
    ...p.serviceCharges.map((c) => c.serviceChargeId),
  ]);
  for (const id of ids) {
    const a = s?.serviceCharges.find((c) => c.serviceChargeId === id);
    const b = p.serviceCharges.find((c) => c.serviceChargeId === id);
    const name = b?.label ?? a?.label ?? 'Extra service';
    rows.push(row('Extra Services', name, num(a?.quantity), num(b?.quantity)));
  }
  return rows;
}

/** True when Extra Services differ: the box then says they're fixed in billing. */
export function extraServicesDiffer(rows: readonly CompareRow[]): boolean {
  return rows.some((r) => r.section === 'Extra Services' && r.differs);
}
